/**
 * Zod schema for the in-memory `Outcome` shape parsed out of an attempt or
 * batch body by the grammar evaluator.
 *
 * Mirrors `../types/outcome.ts`. NOT a persisted row — per `CLAUDE.md §3`
 * ("Outcomes are annotations, not a table"), outcomes live as annotation
 * sections inside attempt/batch body markdown. This schema validates the
 * evaluator-output shape, not a DDL row.
 *
 * Discriminated on `payload.section` per the 4-variant union in the TS type:
 * `rating | notes | tags | generic`.
 */

import { z } from "zod";
import type {
  Outcome,
  OutcomeGeneric,
  OutcomeNotes,
  OutcomeOwner,
  OutcomePayload,
  OutcomeRating,
  OutcomeRatingLine,
  OutcomeTags,
} from "../types/outcome.js";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);

export const OutcomeOwnerSchema: z.ZodType<OutcomeOwner> = z.enum(["attempt", "batch"]);

export const OutcomeRatingLineSchema = z
  .object({
    dimension: z.string(),
    raw: z.string(),
  })
  .strict();

export const OutcomeRatingSchema = z
  .object({
    section: z.literal("rating"),
    lines: z.array(OutcomeRatingLineSchema),
  })
  .strict();

export const OutcomeNotesSchema = z
  .object({
    section: z.literal("notes"),
    body: z.string(),
  })
  .strict();

export const OutcomeTagsSchema = z
  .object({
    section: z.literal("tags"),
    tags: z.array(z.string()),
  })
  .strict();

export const OutcomeGenericLabelledLineSchema = z
  .object({
    label: z.string(),
    value: z.string(),
  })
  .strict();

export const OutcomeGenericSchema = z
  .object({
    section: z.literal("generic"),
    name: z.string(),
    labelledLines: z.array(OutcomeGenericLabelledLineSchema),
    body: z.string(),
  })
  .strict();

export const OutcomePayloadSchema = z.discriminatedUnion("section", [
  OutcomeRatingSchema,
  OutcomeNotesSchema,
  OutcomeTagsSchema,
  OutcomeGenericSchema,
]);

export const OutcomeSchema = z
  .object({
    owner: OutcomeOwnerSchema,
    ownerUuid: uuid,
    headingPath: z.array(z.string()),
    payload: OutcomePayloadSchema,
  })
  .strict();

type _AssertRatingLine =
  z.infer<typeof OutcomeRatingLineSchema> extends OutcomeRatingLine
    ? OutcomeRatingLine extends z.infer<typeof OutcomeRatingLineSchema>
      ? true
      : never
    : never;
type _AssertRating =
  z.infer<typeof OutcomeRatingSchema> extends OutcomeRating
    ? OutcomeRating extends z.infer<typeof OutcomeRatingSchema>
      ? true
      : never
    : never;
type _AssertNotes =
  z.infer<typeof OutcomeNotesSchema> extends OutcomeNotes
    ? OutcomeNotes extends z.infer<typeof OutcomeNotesSchema>
      ? true
      : never
    : never;
type _AssertTags =
  z.infer<typeof OutcomeTagsSchema> extends OutcomeTags
    ? OutcomeTags extends z.infer<typeof OutcomeTagsSchema>
      ? true
      : never
    : never;
type _AssertGeneric =
  z.infer<typeof OutcomeGenericSchema> extends OutcomeGeneric
    ? OutcomeGeneric extends z.infer<typeof OutcomeGenericSchema>
      ? true
      : never
    : never;
type _AssertPayload =
  z.infer<typeof OutcomePayloadSchema> extends OutcomePayload
    ? OutcomePayload extends z.infer<typeof OutcomePayloadSchema>
      ? true
      : never
    : never;
type _AssertOutcome =
  z.infer<typeof OutcomeSchema> extends Outcome
    ? Outcome extends z.infer<typeof OutcomeSchema>
      ? true
      : never
    : never;
export type __OutcomeSchemaAsserts = [
  _AssertRatingLine,
  _AssertRating,
  _AssertNotes,
  _AssertTags,
  _AssertGeneric,
  _AssertPayload,
  _AssertOutcome,
];
